import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Ban, 
  User, 
  ChevronDown, 
  ChevronUp,
  UserX,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

export function BlockedUsersManager() {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [unblocking, setUnblocking] = useState(null);

  const fetchBlockedUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/blocked`, {
        withCredentials: true
      });
      setBlockedUsers(response.data.blocked_users || []);
    } catch (error) {
      // Blocked users list is non-critical - graceful degradation without error display
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to fetch blocked users:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlockedUsers();
  }, [fetchBlockedUsers]);

  const handleUnblock = async (userId, userName) => {
    setUnblocking(userId);
    try {
      await axios.delete(`${API}/community/block/${userId}`, {
        withCredentials: true
      });
      setBlockedUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success(`${userName || "User"} has been unblocked`);
    } catch {
      toast.error("Failed to unblock user");
    } finally {
      setUnblocking(null);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card animate-pulse">
        <CardContent className="p-4">
          <div className="h-10 bg-white/10 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-4">
        <button
          className="w-full flex items-center justify-between"
          onClick={() => setExpanded(!expanded)}
          data-testid="blocked-users-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Ban className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-medium">Blocked Users</h3>
              <p className="text-xs text-white/50">
                {blockedUsers.length === 0 
                  ? "No blocked users" 
                  : `${blockedUsers.length} blocked user${blockedUsers.length !== 1 ? "s" : ""}`
                }
              </p>
            </div>
          </div>
          {blockedUsers.length > 0 && (
            expanded ? (
              <ChevronUp className="w-5 h-5 text-white/40" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/40" />
            )
          )}
        </button>

        {expanded && blockedUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
            {blockedUsers.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                data-testid={`blocked-user-${user.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-white/50" />
                  </div>
                  <span className="text-white/80 text-sm">{user.name || "Anonymous"}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/50 hover:text-white hover:bg-white/10"
                  onClick={() => handleUnblock(user.user_id, user.name)}
                  disabled={unblocking === user.user_id}
                  data-testid={`unblock-btn-${user.user_id}`}
                >
                  {unblocking === user.user_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <UserX className="w-4 h-4 mr-1" />
                      Unblock
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {expanded && blockedUsers.length === 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 text-center py-4">
            <User className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">You haven&apos;t blocked anyone</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BlockedUsersManager;
